import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formEl = e.currentTarget;
    const formData = new FormData(formEl);
    const honeypotVal = (formData.get('website_alt') as string || '').trim();

    if (honeypotVal) {
      setStatus('success');
      setEmail('');
      return;
    }

    if (!email.trim()) return;

    setStatus('loading');
    setErrorMsg('');

    try {
      const submitData = new FormData();
      submitData.append('email', email.trim());

      const response = await fetch('https://readdy.ai/api/form/d9a98m586d4i8fhr8u5g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({ email: email.trim() }).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        if (serverMsg.toLowerCase().includes('spam')) {
          setStatus('success');
          setEmail('');
        } else {
          setStatus('error');
          setErrorMsg(serverMsg || 'Something went wrong. Please try again.');
        }
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please check your connection and try again.');
    }
  };

  return (
    <footer className="w-full bg-foreground-950 text-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-shopping-bag-3-line text-background-50 text-sm"></i>
              </div>
              <span className="font-heading font-semibold text-lg">Superbuy Spreadsheet</span>
            </div>
            <p className="text-foreground-300 text-sm leading-relaxed mb-6">
              Independent educational resource. Helping shoppers discover, compare, and learn — all in one organized hub.
            </p>
            <p className="text-foreground-400 text-xs">
              Not officially affiliated with any platform.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-4 text-background-50">Quick Links</h4>
            <ul className="space-y-2.5">
              <li><Link to="/" className="text-foreground-300 hover:text-primary-400 text-sm transition-colors duration-200">Home</Link></li>
              <li><Link to="/article/superbuy-spreadsheet-complete-beginners-guide" className="text-foreground-300 hover:text-primary-400 text-sm transition-colors duration-200">Beginner Guide</Link></li>
              <li><Link to="/article/superbuy-spreadsheet-vs-traditional-shopping" className="text-foreground-300 hover:text-primary-400 text-sm transition-colors duration-200">Comparison Guide</Link></li>
              <li><Link to="/article/superbuy-spreadsheet-safety-shopping-tips" className="text-foreground-300 hover:text-primary-400 text-sm transition-colors duration-200">Safety Guide</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-4 text-background-50">Top Categories</h4>
            <ul className="space-y-2.5">
              {['Shoes', 'Hoodies / Sweaters', 'Jackets', 'T-Shirts', 'Accessories'].map((cat) => (
                <li key={cat}>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-foreground-300 hover:text-primary-400 text-sm transition-colors duration-200"
                  >
                    {cat}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-4 text-background-50">Stay Updated</h4>
            <p className="text-foreground-300 text-xs leading-relaxed mb-4">
              Get the latest Superbuy Spreadsheet resources and guides delivered to your inbox.
            </p>

            <form onSubmit={handleSubmit} data-readdy-form="true" className="relative" id="footer-newsletter">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-anti-spider"
              />
              <div className="flex">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="flex-1 px-4 py-2.5 bg-foreground-800 border border-foreground-700 rounded-l-lg text-background-50 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="px-4 py-2.5 bg-primary-500 hover:bg-primary-600 rounded-r-lg text-background-50 transition-colors duration-200 cursor-pointer disabled:opacity-60 whitespace-nowrap"
                >
                  {status === 'loading' ? (
                    <i className="ri-loader-4-line animate-spin text-sm"></i>
                  ) : (
                    <i className="ri-arrow-right-line text-sm"></i>
                  )}
                </button>
              </div>
            </form>

            {status === 'success' && (
              <p className="text-accent-400 text-xs mt-2">Thanks for subscribing! Check your inbox soon.</p>
            )}
            {status === 'error' && errorMsg && (
              <p className="text-primary-400 text-xs mt-2">{errorMsg}</p>
            )}
          </div>
        </div>

        <div className="border-t border-foreground-800 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-foreground-400 text-xs">
            &copy; 2026 Superbuy Spreadsheet. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-foreground-400 hover:text-primary-400 text-xs transition-colors">Main Platform</a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-foreground-400 hover:text-primary-400 text-xs transition-colors">Privacy</a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-foreground-400 hover:text-primary-400 text-xs transition-colors">Terms</a>
          </div>
        </div>
      </div>

      <div className="w-full overflow-hidden pb-8 px-4">
        <div className="text-foreground-800 font-heading font-bold text-5xl md:text-7xl lg:text-9xl text-center whitespace-nowrap opacity-10 select-none">
          SUPERBUY SPREADSHEET
        </div>
      </div>
    </footer>
  );
}